import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import classnames from 'classnames';

import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import type { PolymorphicForwardRefRenderFunc, PolymorphicComponentWithRef } from '@hh.ru/magritte-types';
import { City, Female, Male, Undefined } from '@hh.ru/magritte-ui-avatar/Placeholder';
import { AvatarSkeleton } from '@hh.ru/magritte-ui-avatar/Skeleton';
import type { AvatarProps } from '@hh.ru/magritte-ui-avatar/types';
import { AVATAR_TYPOGRAPHY_MAP, formatLetters } from '@hh.ru/magritte-ui-avatar/utils';
import { enrichWithSkeleton } from '@hh.ru/magritte-ui-skeleton';
import { BaseText } from '@hh.ru/magritte-ui-typography/BaseText';

import styles from './avatar.less';

const AvatarForwardRefRenderFunc: PolymorphicForwardRefRenderFunc<AvatarProps, 'div'> = (
    {
        mode,
        size,
        'aria-label': ariaLabel,
        shapeCircle = false,
        online = false,
        styleOnline = 'positive',
        disabled = false,
        letters,
        style,
        icon,
        image,
        fallbackMode,
        placeholder = 'undefined',
        Element = 'div',
        containerDataQa = '',
        ...avatarProps
    },
    ref
) => {
    const [isImageLoaded, setLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    const forceDisabled = useDisabled();

    const isComponentDisabled = disabled || forceDisabled;

    const isModeLetters = mode === 'letters' || (mode === 'image' && fallbackMode === 'letters' && !isImageLoaded);
    const isModeIcon = mode === 'icon';
    const isModePlaceholder =
        mode === 'placeholder' || (mode === 'image' && fallbackMode === 'placeholder' && !isImageLoaded);
    const isModeImage = mode === 'image';

    const additionalAttributes: Record<string, unknown> = {};

    if (Element === 'button') {
        additionalAttributes.type = 'button';
    }

    if (isComponentDisabled && Element === 'a') {
        additionalAttributes['aria-disabled'] = true;
        additionalAttributes.tabIndex = -1;
    }

    if (ref) {
        additionalAttributes.ref = ref;
    }

    const classNames = {
        [styles.avatar]: true,
        [styles.avatarShapeCircle]: shapeCircle,
        [styles.avatarModeImage]: mode === 'image' && isImageLoaded,
        [styles.avatarModeLetters]: isModeLetters,
        [styles.avatarModeIcon]: isModeIcon,
        [styles.avatarModePlaceholder]: isModePlaceholder,
        [styles[`avatar_size-${size}`]]: !!size,
        [styles[`avatar_with-online-badge-${size}`]]: online,
    };

    if ((isModeLetters || isModeIcon) && style) {
        classNames[styles[`avatar_style-${style}`]] = !!style;
    }

    useLayoutEffect(() => {
        const handleOnLoad = () => setLoaded(true);
        const handleOnError = () => setLoaded(false);

        const listenOn = imgRef.current;
        if (imgRef.current && image) {
            imgRef.current.addEventListener('load', handleOnLoad);
            imgRef.current.addEventListener('error', handleOnError);
            imgRef.current.src = image;
        }

        return () => {
            if (listenOn) {
                listenOn.removeEventListener('load', handleOnLoad);
                listenOn.removeEventListener('error', handleOnError);
            }
        };
    }, [image]);

    return (
        <div
            {...(online || isComponentDisabled
                ? {
                      className: classnames({
                          [styles.avatarContainer]: online,
                          [styles.avatarContainerDisabled]: isComponentDisabled,
                          [styles[`avatar-container_size-${size}`]]: online,
                      }),
                  }
                : {})}
            {...(containerDataQa ? { 'data-qa': containerDataQa } : {})}
        >
            <Element
                {...additionalAttributes}
                {...avatarProps}
                className={classnames(classNames)}
                disabled={Element === 'button' ? isComponentDisabled : false}
                aria-label={ariaLabel}
            >
                {isModeLetters && (
                    <BaseText style="primary" Element="span" typography={AVATAR_TYPOGRAPHY_MAP[size]}>
                        {formatLetters(letters || '')}
                    </BaseText>
                )}
                {isModeIcon && icon}
                {isModePlaceholder && (
                    <>
                        {placeholder === 'female' && <Female role="img" width={size} height={size} />}
                        {placeholder === 'male' && <Male role="img" width={size} height={size} />}
                        {placeholder === 'city' && <City role="img" width={size} height={size} />}
                        {placeholder === 'undefined' && <Undefined role="img" width={size} height={size} />}
                    </>
                )}
                {isModeImage && (
                    <img
                        ref={imgRef}
                        alt={ariaLabel}
                        className={classnames(styles.avatarImage, { [styles.avatarImageHidden]: !isImageLoaded })}
                    />
                )}
            </Element>
            {online && (
                <>
                    <div className={styles[`avatar-focus-border-${size}`]} />
                    <div
                        className={classnames(
                            styles[`avatar-online-badge-${size}`],
                            styles[`avatar-online-badge_style_${styleOnline}`]
                        )}
                    />
                </>
            )}
        </div>
    );
};

const AvatarComponent: PolymorphicComponentWithRef<AvatarProps, 'div'> = forwardRef(AvatarForwardRefRenderFunc);

export const Avatar = enrichWithSkeleton(AvatarComponent, AvatarSkeleton);
